import { useState } from 'react'
import { DateTime } from 'luxon'
import {
  faChevronDown,
  faChevronUp,
  faMagnifyingGlass,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons'
import { Button, Icon, Input, Text } from '@/ui'
import { mtc, TextColors, TypographyStyles } from '@/styles'
import type { ConversationSummary } from '@/features/adviser-chat/lib/types'

const historyGroup = (iso: string, today: DateTime): string => {
  const days = today.diff(DateTime.fromISO(iso).startOf('day'), 'days').days
  if (days < 1) return 'Today'
  if (days < 2) return 'Yesterday'
  if (days < 7) return 'Previous 7 days'
  return 'Older'
}

const groupByDay = (conversations: ConversationSummary[]) => {
  const today = DateTime.now().startOf('day')
  const groups: { label: string; items: ConversationSummary[] }[] = []
  for (const conversation of conversations) {
    const label = historyGroup(conversation.updatedAt, today)
    const last = groups.at(-1)
    if (last?.label === label) last.items.push(conversation)
    else groups.push({ label, items: [conversation] })
  }
  return groups
}

interface HistoryRailProps {
  conversations: ConversationSummary[]
  activeConversationId: number | null
  loading?: boolean
  hasMore?: boolean
  onSelect: (id: number) => void
  onLoadMore?: () => void
  onNewConversation?: () => void
}

export const HistoryRail = ({
  conversations,
  activeConversationId,
  loading,
  hasMore,
  onSelect,
  onLoadMore,
  onNewConversation,
}: HistoryRailProps) => {
  const [query, setQuery] = useState('')
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const needle = query.trim().toLowerCase()
  const visible = needle
    ? conversations.filter((c) =>
        (c.title ?? '').toLowerCase().includes(needle)
      )
    : conversations

  const toggleGroup = (label: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (!next.delete(label)) next.add(label)
      return next
    })

  return (
    <div className="flex flex-col min-h-0 gap-5">
      <div className="flex flex-col gap-3">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search"
          aria-label="Search conversations"
          iconLeft={faMagnifyingGlass}
          inputClassName="adviser-search-input bg-neutral-0 placeholder:text-sm"
          className="w-full"
        />
        {onNewConversation && (
          <Button
            variant="secondary"
            size="sm"
            iconLeft={faPlus}
            onClick={onNewConversation}
            className="adviser-new-conversation-btn w-full"
          >
            New conversation
          </Button>
        )}
      </div>
      <div className="flex flex-col min-h-0">
        <div className="flex flex-col gap-4 -mx-3 pb-10 overflow-y-auto min-h-0 [mask-image:linear-gradient(to_bottom,black_calc(100%-40px),transparent)]">
          {groupByDay(visible).map((group) => {
            const open = Boolean(needle) || !collapsed.has(group.label)
            return (
              <section key={group.label} className="flex flex-col gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => toggleGroup(group.label)}
                  aria-expanded={open}
                  className="h-auto w-full justify-between rounded-none bg-transparent px-3 pb-1 pt-0 hover:bg-transparent active:bg-transparent"
                >
                  <Text
                    variant={TypographyStyles.EYEBROW}
                    color={TextColors.MUTED}
                  >
                    {group.label}
                  </Text>
                  <Icon
                    icon={open ? faChevronUp : faChevronDown}
                    color="text-neutral-400"
                    className="text-[12px]"
                  />
                </Button>
                {open && (
                  <ul className="flex flex-col gap-0.5">
                    {group.items.map((conversation) => (
                      <li key={conversation.id}>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => onSelect(conversation.id)}
                          aria-current={
                            conversation.id === activeConversationId
                              ? 'page'
                              : undefined
                          }
                          className={mtc(
                            'h-auto w-full justify-start rounded-[12px] bg-transparent px-3 py-2 text-left hover:bg-moss-50/30 active:bg-moss-50/50',
                            conversation.id === activeConversationId &&
                              'bg-moss-50/50 hover:bg-moss-50/50'
                          )}
                        >
                          <Text
                            variant={TypographyStyles.BODY_SM}
                            color={TextColors.HEADER}
                            className="truncate w-full"
                          >
                            {conversation.title || 'Untitled conversation'}
                          </Text>
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )
          })}
          {hasMore && (
            <Button
              type="button"
              variant="ghost"
              onClick={onLoadMore}
              className="h-auto w-full justify-start rounded-[12px] bg-transparent px-3 py-2 text-left hover:bg-moss-50/30"
            >
              <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
                Load more
              </Text>
            </Button>
          )}
          {!loading && visible.length === 0 && (
            <Text
              variant={TypographyStyles.BODY_XS}
              color={TextColors.MUTED}
              className="px-3 py-2"
            >
              {needle
                ? 'No conversations match that search'
                : 'No conversations yet'}
            </Text>
          )}
        </div>
      </div>
    </div>
  )
}
